import React, { useEffect, useState } from 'react';
import { Alert, AlertDescription, AlertIcon, AlertTitle, Box } from '@chakra-ui/react';
import { Meeting } from '@/services/API';
import { LiveKitConfig } from '../livekit/config';
import { LiveKitVideo } from './LiveKitVideo';

interface MeetingPiPProps {
  meeting: Meeting | null;
  userType: 'ADMIN' | 'HOST' | 'GUEST';
}

/**
 * Meeting Picture-in-Picture component that handles both LiveKit and legacy streams
 * This component replaces the external PipStreamVideo component
 */
export const MeetingPiP: React.FC<MeetingPiPProps> = ({ meeting, userType }) => {
  const [isVisible, setIsVisible] = useState(false);
  if (!meeting) {
    return null;
  }

  const configValidation = LiveKitConfig.validateConfig();
  const isLiveKitConfigured = configValidation.isValid;

  // If LiveKit is not configured, show warning
  if (!isLiveKitConfigured) {
    return (
      <Box position="fixed" top={4} right={4} zIndex={1000} maxW="400px">
        <Alert status="warning" borderRadius="md" boxShadow="lg">
          <AlertIcon />
          <Box>
            <AlertTitle>LiveKit Not Configured</AlertTitle>
            <AlertDescription>
              Video streaming requires LiveKit configuration. Please set up your environment
              variables:
              <br />
              <code>VITE_LIVEKIT_URL</code>, <code>VITE_LIVEKIT_API_KEY</code>,{' '}
              <code>VITE_LIVEKIT_API_SECRET</code>
            </AlertDescription>
          </Box>
        </Alert>
      </Box>
    );
  }

  // Use LiveKit video streaming
  // Determine mode based on user type: HOST/ADMIN = stream, GUEST = join
  const isStreamer = userType === 'HOST' || userType === 'ADMIN';
  const mode = isStreamer ? 'stream' : 'join';

  console.log('🔍 MeetingPiP Debug:', {
    userType,
    isStreamer,
    mode,
    meetingId: meeting.id,
    participantName: `${userType.toLowerCase()}-${meeting.id}`,
  });

  useEffect(() => {
    if (!meeting || !meeting.isStreamingHost) {
      setIsVisible(false);
      return;
    }

    // Show PiP for all user types when meeting is active
    setIsVisible(true);
  }, [meeting]);

  if (!isVisible) {
    return null;
  }

  return (
    <LiveKitVideo
      roomName={`meeting-${meeting.id}`}
      participantName={`${userType.toLowerCase()}-${meeting.id}`}
      mode={mode}
      autoShowPiP
    />
  );
};
